// whitelabelRouteGuard.js — a white-label host never navigates to another
// tenant (WHITELABEL-STRICT-TENANT-SCOPE-1).
//
// Nika 2026-10-15: "this bug is serious and we need to strictly isolate all
// tenants and workspaces. now https://unreal.pr.symbols.app/studio shows keepz
// app". A customer's domain names ONE tenant; a URL on it that names a
// DIFFERENT org — `/w/<other>/…`, `/studio/<other>`, `/workspaces/<other>`, a
// `/<other>/<project>` canvas, or the account's own personal namespace — is
// another tenant's surface rendered under the customer's brand. The earlier
// contract ("a deep link into another org on a white-label host is NOT
// hijacked", customDomainBoot.js) is withdrawn: the host's tenant wins.
//
// smbls router guard (registered via context.router.guards — see
// demoRouteGuard.js / authGuard.js for the convention: `true` allows, a string
// redirects). Running INSIDE the router, before pushState/content-set, means
// the foreign page never mounts at all — no half-rendered board, no fetch of
// the other tenant's data, no title write. The boot-time half (the URL the tab
// LOADS on, before any navigation) is customDomainBoot.js; the data half is
// the server's host lock, which refuses another tenant whatever the URL says.
//
// Fail-safe on the platform: no host pin → `true`, before any parsing.
import { getHostScopeOrgId } from './hostScopePin.js'
import { urlNamedOrgSlug } from './urlOrgSlug.js'

const idOf = (row) => (row ? String(row.id || row._id || '') : '')

// The host org's slug, from what the shell already holds: the (host-scoped)
// org list, or the active org when it IS the host's. '' while unknown.
export const hostOrgSlugOf = (root, hostOrgId) => {
  if (!root || !hostOrgId) return ''
  const rows = Array.isArray(root.orgs) ? root.orgs : []
  const hit =
    rows.find((o) => idOf(o) === String(hostOrgId)) ||
    (root.org && idOf(root.org) === String(hostOrgId) ? root.org : null)
  return (hit && (hit.slug || hit.username)) || ''
}

/**
 * The host's home (`/`) when `pathname` names ANOTHER org on a white-label
 * host, else null. ONE decision for both entry points: the router guard below
 * (every in-app navigation) and the first-render healer (mwtRouter.js
 * mwtOnRenderRouter), which runs before any guard and used to turn a foreign
 * `/w/keepz/default` into the bare route `/default` → "Not found".
 */
export const whitelabelForeignRedirect = (pathname, root) => {
  const hostOrgId = getHostScopeOrgId() || (root && root._hostScopeOrgId) || null
  if (!hostOrgId) return null
  const named = urlNamedOrgSlug(pathname || '', root)
  if (!named) return null
  const hostSlug = hostOrgSlugOf(root, hostOrgId)
  // The host's slug is not known yet (orgs still loading): allow, and let the
  // two layers that need no slug hold the line — the boot reconcile
  // (customDomainBoot.js redirects a foreign URL once orgs land) and the
  // server's host lock (no data of another tenant is served to this origin).
  if (!hostSlug) return null
  // Home of THIS host — the shell canonicalizes `/` onto the host's own
  // workspace. Nothing from the foreign URL (query, hash) is carried over.
  return named === hostSlug ? null : '/'
}

export const whitelabelRouteGuard = ({ pathname, element }) =>
  whitelabelForeignRedirect(pathname, element?.state?.root || element?.state) || true

export default whitelabelRouteGuard
